import * as React from "react";
import { cn } from "../lib/utils";

export interface MailSignaturePreviewProps {
  /** A line of letter above the signature, so the sign-off is seen where it lands. */
  body?: string;
  closing?: string;
  name?: string;
  /** The lines under the name, one per line. */
  details?: string;
  /** The bar between the mark and the name, `#rrggbb`. Empty = a neutral grey. */
  accent?: string;
  /** The mark. Any picture the browser can draw; it is fitted to the mail's own box. */
  logoUrl?: string;
  /** Said in place of the signature when every part of it is empty. */
  emptyLabel?: string;
  className?: string;
}

/** The box the API draws the mark into (`LOGO_BOX` in its `lib/mail/signature.ts`). */
const LOGO_BOX = { width: 96, height: 44 };

const TEXT: React.CSSProperties = { fontSize: 14, lineHeight: 1.5, color: "#555" };

/**
 * The end of a letter as its reader gets it: a line of body, then the signature.
 *
 * A twin of the API's `signatureHtml`, in the same numbers - the closing line,
 * the hairline, the mark, the 3px bar, the name in bold - so a settings screen
 * can show the result while the fields are still being typed in.
 *
 * WHITE ON PURPOSE, in the dark theme too: this is a picture of a letter, and
 * the letter is drawn on the reader's mail program's page, not on this app's.
 */
export function MailSignaturePreview({
  body,
  closing,
  name,
  details,
  accent,
  logoUrl,
  emptyLabel,
  className,
}: MailSignaturePreviewProps) {
  const lines = (details ?? "").split("\n").map((l) => l.trim()).filter(Boolean);
  const hasWords = Boolean(name?.trim() || lines.length);
  const hasCard = hasWords || Boolean(logoUrl);
  const empty = !closing?.trim() && !hasCard;

  return (
    <div
      className={cn("rounded-md border border-border px-5 py-4", className)}
      style={{ background: "#fff", color: "#111", fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif" }}
    >
      {body ? <p style={{ fontSize: 14, lineHeight: 1.5, margin: 0 }}>{body}</p> : null}

      {empty ? (
        emptyLabel ? <p style={{ ...TEXT, margin: "28px 0 0", fontStyle: "italic", color: "#888" }}>{emptyLabel}</p> : null
      ) : (
        <div style={{ marginTop: body ? 28 : 0 }}>
          {closing?.trim() ? <div style={TEXT}>{closing.trim()}</div> : null}
          {hasCard ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                borderTop: "1px solid #e5e5e5",
                marginTop: closing?.trim() ? 12 : 0,
                paddingTop: 16,
              }}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=""
                  style={{
                    display: "block",
                    flex: "none",
                    maxWidth: LOGO_BOX.width,
                    maxHeight: LOGO_BOX.height,
                    marginRight: hasWords ? 16 : 0,
                  }}
                />
              ) : null}
              {hasWords ? (
                <div style={{ minWidth: 0, borderLeft: `3px solid ${accent || "#d4d4d4"}`, paddingLeft: 14 }}>
                  {name?.trim() ? (
                    <div style={{ fontSize: 16, lineHeight: 1.3, fontWeight: 700, color: "#111", overflowWrap: "anywhere" }}>
                      {name.trim()}
                    </div>
                  ) : null}
                  {lines.length ? (
                    <div style={{ ...TEXT, marginTop: name?.trim() ? 4 : 0, overflowWrap: "anywhere" }}>
                      {lines.map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
